/**
 * Token Service
 *
 * SPL token operations: mint info, token-account enumeration, and token
 * transfers via Kit + @solana-program/token. Uses the shared RPC from
 * `solanaService`.
 */

import {
  appendTransactionMessageInstructions,
  compileTransaction,
  createTransactionMessage,
  setTransactionMessageFeePayer,
  setTransactionMessageLifetimeUsingBlockhash,
  type Address,
} from "@solana/kit";
import {
  findAssociatedTokenPda,
  getCreateAssociatedTokenIdempotentInstruction,
  getTransferInstruction,
  TOKEN_PROGRAM_ADDRESS,
} from "@solana-program/token";
import { TOKEN_2022_PROGRAM_ADDRESS } from "@solana-program/token-2022";
import { solanaService } from "./solana";
import { API_TIMEOUT } from "@/config/constants";
import type { TokenAccount, TransactionResult } from "@/types/solana";

import { transactionRelay, decimalToBaseUnits, type SignTransactionFn } from "./transaction-relay";

interface ParsedTokenAccountInfo {
  mint: string;
  owner: string;
  tokenAmount: {
    amount: string;
    decimals: number;
    uiAmount: number | null;
    uiAmountString: string | null;
  };
}

class TokenService {
  /** Fetch mint info (decimals, supply) via parsed account. */
  async getTokenMintInfo(
    mint: Address,
  ): Promise<{ decimals: number; supply: bigint } | null> {
    const rpc = solanaService.getRpc();
    try {
      const { value } = await rpc
        .getAccountInfo(mint, { encoding: "jsonParsed" })
        .send();
      if (!value) return null;
      // Parsed mint account: value.data.parsed.info has { decimals, supply, ... }
      const data = value.data as {
        parsed?: { info?: { decimals?: number; supply?: string } };
      };
      const info = data.parsed?.info;
      if (info?.decimals === undefined || info.supply === undefined) return null;
      return { decimals: info.decimals, supply: BigInt(info.supply) };
    } catch {
      return null;
    }
  }

  /**
   * All SPL token accounts owned by `owner` across Token Program and Token-2022.
   */
  async getTokenAccounts(owner: Address): Promise<TokenAccount[]> {
    const rpc = solanaService.getRpc();
    const accounts: TokenAccount[] = [];

    for (const programId of [
      TOKEN_PROGRAM_ADDRESS,
      TOKEN_2022_PROGRAM_ADDRESS,
    ]) {
      const { value } = await rpc
        .getTokenAccountsByOwner(
          owner,
          { programId },
          { encoding: "jsonParsed" },
        )
        .send();

      for (const entry of value) {
        const parsed = entry.account.data as {
          parsed?: { info?: ParsedTokenAccountInfo };
        };
        const info = parsed.parsed?.info;
        if (!info) continue;
        accounts.push({
          pubkey: entry.pubkey,
          mint: info.mint as Address,
          owner: info.owner as Address,
          amount: BigInt(info.tokenAmount.amount),
          decimals: info.tokenAmount.decimals,
          uiAmount: info.tokenAmount.uiAmount ?? 0,
          uiAmountString: info.tokenAmount.uiAmountString ?? "0",
          programId,
        });
      }
    }

    return accounts;
  }

  /**
   * Transfer SPL tokens. Creates the recipient's associated token account
   * idempotently if it doesn't exist.
   */
  async sendToken(
    from: Address,
    to: Address,
    mint: Address,
    amount: number | string,
    decimals: number,
    signTransaction: SignTransactionFn,
  ): Promise<TransactionResult> {
    const rpc = solanaService.getRpc();
    return transactionRelay.execute(
      from,
      { recipient: to, amount: String(amount), asset: mint },
      async () => {
        const { value: blockhashInfo } = await rpc.getLatestBlockhash().send({ abortSignal: AbortSignal.timeout(API_TIMEOUT) });

        const [fromAta] = await findAssociatedTokenPda({
          owner: from,
          mint,
          tokenProgram: TOKEN_PROGRAM_ADDRESS,
        });
        const [toAta] = await findAssociatedTokenPda({
          owner: to,
          mint,
          tokenProgram: TOKEN_PROGRAM_ADDRESS,
        });

        const rawAmount = decimalToBaseUnits(amount, decimals);

        const createAtaIx = getCreateAssociatedTokenIdempotentInstruction({
          payer: { address: from, signTransactions: async () => [] } as never,
          ata: toAta,
          owner: to,
          mint,
        });

        const transferIx = getTransferInstruction({
          source: fromAta,
          destination: toAta,
          authority: { address: from, signTransactions: async () => [] } as never,
          amount: rawAmount,
        });

        const txMsg = createTransactionMessage({ version: 0 });
        const withPayer = setTransactionMessageFeePayer(from, txMsg);
        const withLifetime = setTransactionMessageLifetimeUsingBlockhash(
          blockhashInfo,
          withPayer,
        );
        const withIxs = appendTransactionMessageInstructions(
          [createAtaIx, transferIx],
          withLifetime,
        );
        return compileTransaction(withIxs);
      },
      signTransaction,
      rpc,
    );
  }
}

export const tokenService = new TokenService();
